import { useCallback } from 'react';

import { useNotification } from '@hooks/useNotification';

interface OperationService {
  delete: (id: number) => Promise<any>;
  duplicate?: (id: number) => Promise<any>;
  approve?: (id: number) => Promise<any>;
  activate?: (id: number) => Promise<any>;
  suspend?: (id: number) => Promise<any>;
  end?: (id: number) => Promise<any>;
  post?: (id: number) => Promise<any>;
  posted?: (id: number) => Promise<any>;
  void?: (id: number) => Promise<any>;
  convertToInvoice?: (id: number) => Promise<any>;
}

interface UseGridOperationsOptions {
  service: OperationService;
  refreshGrid: (resetToFirstPage?: boolean) => void;
  entityName?: string;
}

export const useGridOperations = ({
  service,
  refreshGrid,
  entityName = 'record',
}: UseGridOperationsOptions) => {
  const { showSuccess, showError } = useNotification();

  const handleDelete = useCallback(
    async (id: number, itemName?: string) => {
      try {
        const result = await service.delete(id);
        if (result.isSuccess) {
          showSuccess(`Successfully deleted ${itemName || entityName}.`);
          refreshGrid(true); // Reset to page 1
        } else {
          showError(
            `Failed to delete ${itemName || entityName}: ${
              result.errorMessages
            }`
          );
        }
      } catch (error) {
        showError(`Error deleting ${itemName || entityName}: ${error}`);
      }
    },
    [service, refreshGrid, showSuccess, showError, entityName]
  );

  const handleDuplicate = useCallback(
    async (id: number, itemName?: string) => {
      if (!service.duplicate) return;

      try {
        const result = await service.duplicate(id);
        if (result.isSuccess) {
          showSuccess(`Successfully duplicated ${itemName || entityName}.`);
          refreshGrid(true); // Reset to page 1
        } else {
          showError(
            `Failed to duplicate ${itemName || entityName}: ${
              result.errorMessages
            }`
          );
        }
      } catch (error) {
        showError(`Error duplicating ${itemName || entityName}: ${error}`);
      }
    },
    [service, refreshGrid, showSuccess, showError, entityName]
  );

  const handleApprove = useCallback(
    async (id: number, itemName?: string) => {
      if (!service.approve) return;

      try {
        const result = await service.approve(id);
        if (result.isSuccess) {
          showSuccess(`Successfully approved ${itemName || entityName}.`);
          refreshGrid(true); // Reset to page 1
        } else {
          showError(
            `Failed to approve ${itemName || entityName}: ${
              result.errorMessages
            }`
          );
        }
      } catch (error) {
        showError(`Error approving ${itemName || entityName}: ${error}`);
      }
    },
    [service, refreshGrid, showSuccess, showError, entityName]
  );

  const handleActivate = useCallback(
    async (id: number, itemName?: string) => {
      if (!service.activate) return;

      try {
        const result = await service.activate(id);
        if (result.isSuccess) {
          showSuccess(`Successfully activated ${itemName || entityName}.`);
          refreshGrid(true); // Reset to page 1
        } else {
          showError(
            `Failed to activate ${itemName || entityName}: ${
              result.errorMessages
            }`
          );
        }
      } catch (error) {
        showError(`Error activating ${itemName || entityName}: ${error}`);
      }
    },
    [service, refreshGrid, showSuccess, showError, entityName]
  );

  const handleSuspend = useCallback(
    async (id: number, itemName?: string) => {
      if (!service.suspend) return;

      try {
        const result = await service.suspend(id);
        if (result.isSuccess) {
          showSuccess(`Successfully suspended ${itemName || entityName}.`);
          refreshGrid(true); // Reset to page 1
        } else {
          showError(
            `Failed to suspend ${itemName || entityName}: ${
              result.errorMessages
            }`
          );
        }
      } catch (error) {
        showError(`Error suspending ${itemName || entityName}: ${error}`);
      }
    },
    [service, refreshGrid, showSuccess, showError, entityName]
  );

  const handleEnd = useCallback(
    async (id: number, itemName?: string) => {
      if (!service.end) return;

      try {
        const result = await service.end(id);
        if (result.isSuccess) {
          showSuccess(`Successfully ended ${itemName || entityName}.`);
          refreshGrid(true); // Reset to page 1
        } else {
          showError(
            `Failed to end ${itemName || entityName}: ${result.errorMessages}`
          );
        }
      } catch (error) {
        showError(`Error ending ${itemName || entityName}: ${error}`);
      }
    },
    [service, refreshGrid, showSuccess, showError, entityName]
  );

  const handlePost = useCallback(
    async (id: number, itemName?: string) => {
      if (!service.post) return;

      try {
        const result = await service.post(id);
        if (result.isSuccess) {
          showSuccess(`Successfully posted ${itemName || entityName}.`);
          refreshGrid(true); // Reset to page 1
        } else {
          showError(
            `Failed to post ${itemName || entityName}: ${result.errorMessages}`
          );
        }
      } catch (error) {
        showError(`Error posting ${itemName || entityName}: ${error}`);
      }
    },
    [service, refreshGrid, showSuccess, showError, entityName]
  );

  const handlePosted = useCallback(
    async (id: number, itemName?: string) => {
      if (!service.posted) return;

      try {
        const result = await service.posted(id);
        if (result.isSuccess) {
          showSuccess(
            `Successfully changed ${itemName || entityName} status to posted.`
          );
          refreshGrid(true); // Reset to page 1
        } else {
          showError(
            `Failed to change ${itemName || entityName} status to posted: ${
              result.errorMessages
            }`
          );
        }
      } catch (error) {
        showError(
          `Error changing ${itemName || entityName} status to posted: ${error}`
        );
      }
    },
    [service, refreshGrid, showSuccess, showError, entityName]
  );

  const handleVoid = useCallback(
    async (id: number, itemName?: string) => {
      if (!service.void) return;

      try {
        const result = await service.void(id);
        if (result.isSuccess) {
          showSuccess(`Successfully voided ${itemName || entityName}.`);
          refreshGrid(true); // Reset to page 1
        } else {
          showError(
            `Failed to void ${itemName || entityName}: ${result.errorMessages}`
          );
        }
      } catch (error) {
        showError(`Error voiding ${itemName || entityName}: ${error}`);
      }
    },
    [service, refreshGrid, showSuccess, showError, entityName]
  );

  const handleConvertToInvoice = useCallback(
    async (id: number, itemName?: string) => {
      if (!service.convertToInvoice) return;

      try {
        const result = await service.convertToInvoice(id);
        if (result.isSuccess) {
          showSuccess(
            `Successfully converted ${itemName || entityName} to invoice.`
          );
          refreshGrid(true); // Reset to page 1
        } else {
          showError(
            `Failed to convert ${itemName || entityName} to invoice: ${
              result.errorMessages
            }`
          );
        }
      } catch (error) {
        showError(
          `Error converting ${itemName || entityName} to invoice: ${error}`
        );
      }
    },
    [service, refreshGrid, showSuccess, showError, entityName]
  );

  return {
    handleDelete,
    handleDuplicate,
    handleApprove,
    handleActivate,
    handleSuspend,
    handleEnd,
    handlePost,
    handlePosted,
    handleVoid,
    handleConvertToInvoice,
  };
};
